import { useHistory, useLocation } from "react-router-dom";
import { Button } from "@mui/material";
import useTranslate from "hooks/use-translate-language";
import { formatDate } from "utils/misc";
import "./styles.scss";

const ThankYouPage = () => {
  const translate = useTranslate();
  const history = useHistory();
  const location = useLocation();

  const message = location.state?.message;
  const shouldPostMessage = location.state?.shouldPostMessage;
  const subMessage = location.state?.subMessage;
  const backTo = location.state?.backTo;
  const allProductLines = location.state?.allProductLines || [];
  const currentlyOrderedProductLine =
    location.state?.currentlyOrderedProductLine;
  const patientHCPCHistory = location.state?.patientHCPCHistory || [];
  const origin = location.state?.origin;
  const token = location.state?.token;

  // Filter out only the currently ordered product line
  const availableProductLines = allProductLines.filter(
    line => line.text !== currentlyOrderedProductLine
  );

  // Push message to rotech/tekstream portal web app and mobile app to signal that modal window/iframe should close
  function rotechPortalPushMessage() {
    try {
      const message = {
        source: "S3Portal",
        message: "Order Form Submitted"
      };

      if (window && window.parent) {
        window.parent.postMessage(message, "*");
      }

      if (window.ReactNativeWebView.postMessage) {
        window.ReactNativeWebView.postMessage.postMessage(message, "*");
      }
    } catch (err) {
      window.newrelic?.noticeError(err);
    }
  }
  return (
    <div className="thanks-screen">
      <div className="thanks-container">
        <h1>{translate("thankYou")}</h1>
        <h3>{message}</h3>
        <h4 className="sub-message">{subMessage}</h4>
        <div className="close-btn-container">
          {shouldPostMessage && (
            <Button
              onClick={rotechPortalPushMessage}
              variant="contained"
              color="primary"
            >
              Close
            </Button>
          )}
        </div>
        {availableProductLines.length > 0 && (
          <div
            style={{
              marginTop: 24,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flex: 1
            }}
          >
            {availableProductLines.map(line => {
              // Helper function matching the non-patient facing app
              const getMostRecentEligibleItem = productLine => {
                const itemsForProductLine = patientHCPCHistory?.filter(item => {
                  return (
                    item.productLine === productLine &&
                    item.productType !== "Machine"
                  );
                });
                if (itemsForProductLine?.length === 0)
                  return { isEligible: true };

                const sortedItems = itemsForProductLine.sort((a, b) => {
                  const dateA = new Date(a.eligibilityDate);
                  const dateB = new Date(b.eligibilityDate);
                  return dateA - dateB;
                });

                const mostRecent = sortedItems[0];
                return mostRecent;
              };

              // Helper function to get eligibility status display matching the non-patient facing app
              const getEligibilityStatus = item => {
                if (!item || item.isEligible) {
                  return {
                    text: "✓ Currently Eligible",
                    color: "#28a745",
                    bgColor: "#e8f5e8",
                    borderColor: "#28a745"
                  };
                } else if (item.isSoonEligible) {
                  return {
                    text: "⏰ Eligible Soon",
                    color: "#ffc107",
                    bgColor: "#fff3cd",
                    borderColor: "#ffc107"
                  };
                } else {
                  return {
                    text: "⚠ Not Currently Eligible",
                    color: "#dc3545",
                    bgColor: "#ffe6e6",
                    borderColor: "#dc3545"
                  };
                }
              };

              let earliestEligibleItem = null;
              let eligibilityStatus = null;

              // If patientHCPCHistory is available, use new logic, otherwise fall back to old logic
              if (patientHCPCHistory && patientHCPCHistory.length > 0) {
                earliestEligibleItem = getMostRecentEligibleItem(line.text);
                eligibilityStatus = getEligibilityStatus(earliestEligibleItem);
              }
              if (
                earliestEligibleItem.isEligible ||
                earliestEligibleItem.isSoonEligible
              ) {
                return (
                  <div
                    key={line.text}
                    style={{
                      margin: "20px 0",
                      padding: "20px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      backgroundColor: earliestEligibleItem?.isEligible
                        ? "#f0f8f0"
                        : "#fff",
                      textAlign: "center"
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: "bold",
                        marginBottom: 10
                      }}
                    >
                      Would you like to place an order for your {line.text}{" "}
                      supplies?
                    </div>

                    {eligibilityStatus && (
                      <div
                        style={{
                          fontSize: "14px",
                          color: eligibilityStatus.color,
                          fontWeight: "bold",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          backgroundColor: eligibilityStatus.bgColor,
                          border: `1px solid ${eligibilityStatus.borderColor}`,
                          display: "inline-block",
                          marginBottom: 10
                        }}
                      >
                        {eligibilityStatus.text}
                      </div>
                    )}

                    {patientHCPCHistory &&
                      patientHCPCHistory.length > 0 &&
                      !earliestEligibleItem?.isEligible && (
                        <div style={{ marginBottom: 15, fontSize: 16 }}>
                          <strong>Eligibility Date:</strong>{" "}
                          <span
                            style={{
                              color: eligibilityStatus?.color || "black",
                              fontWeight: "bold"
                            }}
                          >
                            {formatDate(
                              earliestEligibleItem?.eligibilityDate,
                              "MM/DD/YYYY"
                            )}
                          </span>
                        </div>
                      )}

                    {(!patientHCPCHistory ||
                      patientHCPCHistory.length === 0) && (
                      <div
                        style={{
                          fontSize: 16,
                          color: "#666",
                          marginBottom: 15
                        }}
                      >
                        Eligible On:{" "}
                        {formatDate(line.outreach_date, "MM/DD/YYYY")}
                      </div>
                    )}

                    <Button
                      onClick={() => {
                        // Use /order-form for external/SMS containers, /supplies for app container
                        if (origin === "Patient Portal") {
                          history.push({
                            pathname: "/supplies",
                            state: { nextProductLine: line.text }
                          });
                        } else {
                          // For non-Patient Portal origins, include token and origin as query parameters
                          const queryParams = new URLSearchParams();
                          if (token) queryParams.append("token", token);
                          if (origin) queryParams.append("origin", origin);
                          // Bypass DOB validation for additional orders
                          queryParams.append("ref", "continue");

                          history.push({
                            pathname: "/order-form",
                            search: queryParams.toString(),
                            state: { nextProductLine: line.text }
                          });
                        }
                      }}
                      variant="contained"
                      color="primary"
                      size="large"
                      style={{
                        margin: "10px 0",
                        padding: "12px 30px",
                        fontSize: 16
                      }}
                    >
                      Order {line.text} Supplies
                    </Button>
                  </div>
                );
              } else return null;
            })}
          </div>
        )}
        {backTo && (
          <Button
            onClick={() => {
              if (backTo == "previous") history.goBack();
              else history.push(backTo);
            }}
            variant="contained"
            color="primary"
          >
            {translate("goBack")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ThankYouPage;
